import { useMutation } from '@tanstack/react-query';
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';
import { ChevronDown, ChevronRight, FileUp, Search, Wand2 } from 'lucide-react';
import { Fragment, useMemo, useState } from 'react';
import { ConfidenceChip } from '@/components/ai-accountant/confidence-chip';
import { LineEditor, useInvalidateBanking } from '@/components/banking/line-editor';
import { ErrorBanner } from '@/components/form';
import { EmptyState, PageHeader } from '@/components/page-header';
import { toast } from '@/components/toast';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { useAccountNames } from '@/lib/accounts';
import { api } from '@/lib/api';
import {
  AI_ACCEPT_CONFIDENCE,
  useBankAccounts,
  useBankLines,
  type AiSuggestion,
  type BankAccount,
  type LineStatus,
  type StatementLine,
} from '@/lib/banking';
import { useMe } from '@/lib/clients';
import { displayDate } from '@/lib/dates';
import { formatAmount } from '@/lib/format';
import { useT } from '@/lib/i18n';
import { useOrgId } from '@/lib/org';
import { cn } from '@/lib/utils';

interface Search {
  accountId?: string;
  tab?: LineStatus;
}

export const Route = createFileRoute('/banking/')({
  component: BankTransactionsPage,
  validateSearch: (s: Record<string, unknown>): Search => ({
    ...(typeof s['accountId'] === 'string' ? { accountId: s['accountId'] } : {}),
    ...(s['tab'] === 'for_review' || s['tab'] === 'categorised' || s['tab'] === 'excluded'
      ? { tab: s['tab'] }
      : {}),
  }),
});

const TABS: LineStatus[] = ['for_review', 'categorised', 'excluded'];

/** Screen B1 (document 06): Bank transactions. */
function BankTransactionsPage() {
  const t = useT();
  const orgId = useOrgId();
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const banks = useBankAccounts();
  const me = useMe();
  const canEdit = me.data?.isStaff ?? false;
  // the account with most to review, unless one was chosen
  const selected =
    banks.data?.find((b) => b.accountId === search.accountId) ??
    [...(banks.data ?? [])].sort((a, b) => b.forReview - a.forReview)[0] ??
    null;
  const tab = search.tab ?? 'for_review';

  return (
    <>
      <PageHeader
        title={t('purchasePages.bankFeed.title')}
        actions={
          <>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline">
                  {t('purchasePages.bankFeed.more')} <ChevronDown className="size-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem asChild>
                  <Link to="/banking/rules">{t('purchasePages.bankFeed.manageRules')}</Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/banking/reconcile">{t('purchasePages.bankFeed.reconcile')}</Link>
                </DropdownMenuItem>
                {canEdit ? (
                  <DropdownMenuItem asChild>
                    <Link to="/banking/transfers/new">
                      {t('purchasePages.bankFeed.recordTransfer')}
                    </Link>
                  </DropdownMenuItem>
                ) : null}
                {selected ? (
                  <DropdownMenuItem asChild>
                    <Link
                      to="/accounting/chart-of-accounts/$accountId/register"
                      params={{ accountId: selected.accountId }}
                    >
                      {t('purchasePages.bankFeed.goToRegister')}
                    </Link>
                  </DropdownMenuItem>
                ) : null}
              </DropdownMenuContent>
            </DropdownMenu>
            {canEdit ? (
              <Button asChild>
                <Link
                  to="/banking/import"
                  search={selected ? { accountId: selected.accountId } : {}}
                >
                  <FileUp className="size-4" /> {t('purchasePages.bankFeed.importStatement')}
                </Link>
              </Button>
            ) : null}
          </>
        }
      />
      {!orgId ? (
        <EmptyState
          title={t('shell.selectClient')}
          hint={t('purchasePages.bankFeed.selectClientHint')}
        />
      ) : banks.isPending ? (
        <p className="text-sm text-muted-foreground">{t('common.loading')}</p>
      ) : banks.isError ? (
        <EmptyState
          title={t('purchasePages.bankFeed.loadAccountsFailed')}
          hint={banks.error.message}
        />
      ) : !selected ? (
        <EmptyState
          title={t('purchasePages.bankFeed.noAccounts')}
          hint={t('purchasePages.bankFeed.noAccountsHint')}
          action={
            <Button asChild>
              <Link to="/accounting/chart-of-accounts">
                {t('purchasePages.bankFeed.chartOfAccounts')}
              </Link>
            </Button>
          }
        />
      ) : (
        <>
          <BankTiles
            banks={banks.data}
            selected={selected.accountId}
            onSelect={(accountId) => void navigate({ search: { accountId, tab } })}
          />
          <nav
            className="mb-3 flex gap-6 border-b text-sm"
            aria-label={t('purchasePages.bankFeed.tabsAria')}
          >
            {TABS.map((key) => (
              <button
                key={key}
                type="button"
                aria-current={tab === key ? 'page' : undefined}
                className={cn(
                  '-mb-px border-b-2 px-1 pb-2 font-medium',
                  tab === key
                    ? 'border-primary text-primary'
                    : 'border-transparent text-muted-foreground hover:text-foreground',
                )}
                onClick={() =>
                  void navigate({ search: { accountId: selected.accountId, tab: key } })
                }
              >
                {key === 'for_review'
                  ? t('purchasePages.bankFeed.forReviewCount', { count: selected.forReview })
                  : t(`purchasePages.bankFeed.tab.${key}`)}
              </button>
            ))}
          </nav>
          <LinesTable
            key={`${selected.accountId}-${tab}`}
            bank={selected}
            banks={banks.data}
            tab={tab}
            canEdit={canEdit}
          />
        </>
      )}
    </>
  );
}

function BankTiles({
  banks,
  selected,
  onSelect,
}: {
  banks: BankAccount[];
  selected: string;
  onSelect: (accountId: string) => void;
}) {
  const t = useT();
  const names = useAccountNames();
  return (
    <div
      className="mb-5 flex gap-3 overflow-x-auto pb-1"
      role="tablist"
      aria-label={t('purchasePages.bankFeed.accountsAria')}
    >
      {banks.map((b) => (
        <button
          key={b.accountId}
          type="button"
          role="tab"
          aria-selected={b.accountId === selected}
          onClick={() => onSelect(b.accountId)}
          className={cn(
            'flex w-[260px] shrink-0 flex-col gap-2 rounded-lg border bg-card p-4 text-start text-sm',
            b.accountId === selected
              ? 'border-primary ring-2 ring-primary/20'
              : 'hover:border-foreground/30',
          )}
        >
          <div>
            <div className="truncate font-semibold">{names.name(b.accountId, b.name)}</div>
            <div className="truncate text-xs text-muted-foreground">
              {b.bankName ?? names.detailTypeName(b.accountId, b.detailTypeName)}
              {b.accountNumber ? ` · ${b.accountNumber}` : ''}
            </div>
          </div>
          <div className="flex items-baseline justify-between gap-2">
            <span className="text-xs text-muted-foreground">
              {t('purchasePages.bankFeed.bankBalance')}
            </span>
            <span className="tnum font-medium">
              {b.bankBalance === null ? '—' : `${b.currency} ${formatAmount(b.bankBalance)}`}
            </span>
          </div>
          <div className="flex items-baseline justify-between gap-2">
            <span className="text-xs text-muted-foreground">
              {t('purchasePages.bankFeed.inOutss')}
            </span>
            <span className="tnum font-medium">
              {b.currency} {formatAmount(b.ledgerBalance)}
            </span>
          </div>
          {b.currency !== b.homeCurrency ? (
            <div className="flex items-baseline justify-between gap-2 text-xs text-muted-foreground">
              <span>{t('purchasePages.bankFeed.inCurrency', { currency: b.homeCurrency })}</span>
              <span className="tnum" data-testid="home-ledger-balance">
                {b.homeCurrency} {formatAmount(b.homeLedgerBalance)}
              </span>
            </div>
          ) : null}
          <div className="flex items-center justify-between text-xs">
            <span
              className={cn(b.forReview ? 'font-medium text-primary' : 'text-muted-foreground')}
            >
              {t('purchasePages.bankFeed.toReview', { count: b.forReview })}
            </span>
            <span className="text-muted-foreground">
              {b.lastImportAt
                ? t('purchasePages.bankFeed.imported', {
                    date: displayDate(b.lastImportAt.slice(0, 10)),
                  })
                : t('purchasePages.bankFeed.noStatement')}
            </span>
          </div>
        </button>
      ))}
    </div>
  );
}

function LinesTable({
  bank,
  banks,
  tab,
  canEdit,
}: {
  bank: BankAccount;
  banks: BankAccount[];
  tab: LineStatus;
  canEdit: boolean;
}) {
  const t = useT();
  const orgId = useOrgId();
  const invalidate = useInvalidateBanking();
  const lines = useBankLines(bank.accountId, tab);
  const [q, setQ] = useState('');
  const [open, setOpen] = useState<string | null>(null);
  const [checked, setChecked] = useState<Set<string>>(new Set());

  const needle = q.trim().toLowerCase();
  const rows = useMemo(
    () =>
      (lines.data ?? []).filter(
        (l) =>
          !needle ||
          [l.description, l.payee, l.reference, l.contactName, l.categoryLabel, l.memo]
            .filter(Boolean)
            .some((v) => v!.toLowerCase().includes(needle)) ||
          (l.spent ?? l.received ?? '').includes(needle.replace(/,/g, '')),
      ),
    [lines.data, needle],
  );

  const batch = useMutation({
    mutationFn: (body: { action: 'accept' | 'exclude' | 'undo'; ids: string[] }) =>
      api<{ done: number; skipped: number; failed: Array<{ id: string; message: string }> }>(
        `/orgs/${orgId}/banking/lines/batch`,
        { method: 'POST', body: JSON.stringify(body) },
      ),
    onSuccess: async (r, body) => {
      setChecked(new Set());
      await invalidate();
      toast(
        t(`purchasePages.bankFeed.batchDone.${body.action}`, { count: r.done }) +
          (r.skipped
            ? t(`purchasePages.bankFeed.batchSkipped.${body.action}`, { count: r.skipped })
            : '') +
          (r.failed.length
            ? t('purchasePages.bankFeed.batchFailed', {
                count: r.failed.length,
                message: r.failed[0]!.message,
              })
            : '') +
          t('purchasePages.bankFeed.batchEnd'),
        { tone: r.failed.length ? 'error' : 'success' },
      );
    },
  });
  const single = (action: 'accept' | 'undo', id: string) => batch.mutate({ action, ids: [id] });

  const allChecked = rows.length > 0 && rows.every((r) => checked.has(r.id));
  const toggleAll = () => setChecked(allChecked ? new Set() : new Set(rows.map((r) => r.id)));
  const toggle = (id: string) =>
    setChecked((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });

  return (
    <>
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <div className="relative">
          <Search className="absolute start-2.5 top-2.5 size-4 text-muted-foreground" />
          <Input
            aria-label={t('purchasePages.bankFeed.searchAria')}
            placeholder={t('purchasePages.bankFeed.searchPlaceholder')}
            className="w-[300px] ps-8"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
        </div>
        {canEdit && checked.size > 0 ? (
          <div className="flex items-center gap-2" data-testid="batch-actions">
            <span className="text-sm text-muted-foreground">
              {t('purchasePages.bankFeed.selectedCount', { count: checked.size })}
            </span>
            {tab === 'for_review' ? (
              <>
                <Button
                  size="sm"
                  disabled={batch.isPending}
                  onClick={() => batch.mutate({ action: 'accept', ids: [...checked] })}
                >
                  {t('purchasePages.bankFeed.accept')}
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  disabled={batch.isPending}
                  onClick={() => batch.mutate({ action: 'exclude', ids: [...checked] })}
                >
                  {t('purchasePages.bankLabels.exclude')}
                </Button>
              </>
            ) : (
              <Button
                size="sm"
                variant="outline"
                disabled={batch.isPending}
                onClick={() => batch.mutate({ action: 'undo', ids: [...checked] })}
              >
                {t('purchasePages.bankLabels.undo')}
              </Button>
            )}
          </div>
        ) : null}
        {tab === 'for_review' ? (
          <Link
            to="/banking/rules"
            className="ms-auto inline-flex items-center gap-1 text-sm text-primary hover:underline"
          >
            <Wand2 className="size-4" /> {t('purchasePages.bankFeed.rules')}
          </Link>
        ) : null}
      </div>
      <ErrorBanner>{batch.error?.message ?? null}</ErrorBanner>
      {lines.isPending ? (
        <p className="text-sm text-muted-foreground">{t('common.loading')}</p>
      ) : lines.isError ? (
        <EmptyState
          title={t('purchasePages.bankFeed.loadLinesFailed')}
          hint={lines.error.message}
        />
      ) : rows.length === 0 ? (
        <EmptyState
          title={
            needle
              ? t('purchasePages.bankFeed.noSearchMatch')
              : tab === 'for_review'
                ? t('purchasePages.bankFeed.nothingToReview')
                : tab === 'categorised'
                  ? t('purchasePages.bankFeed.noCategorised')
                  : t('purchasePages.bankFeed.noExcluded')
          }
          {...(tab === 'for_review' && !needle
            ? { hint: t('purchasePages.bankFeed.importHint') }
            : {})}
        />
      ) : (
        <div className="rounded-lg border bg-card">
          <Table>
            <TableHeader className="sticky top-0 bg-card">
              <TableRow>
                {canEdit ? (
                  <TableHead className="w-10">
                    <input
                      type="checkbox"
                      className="size-4 accent-primary"
                      aria-label={t('purchasePages.bankFeed.selectAll')}
                      checked={allChecked}
                      onChange={toggleAll}
                    />
                  </TableHead>
                ) : null}
                <TableHead className="w-[110px]">{t('purchasePages.bankFeed.col.date')}</TableHead>
                <TableHead>{t('purchasePages.bankFeed.col.description')}</TableHead>
                <TableHead className="w-[180px]">{t('purchasePages.bankFeed.col.payee')}</TableHead>
                <TableHead className="w-[260px]">
                  {tab === 'for_review'
                    ? t('purchasePages.bankFeed.col.categoryOrMatch')
                    : tab === 'categorised'
                      ? t('purchasePages.bankFeed.col.addedOrMatched')
                      : t('purchasePages.bankFeed.col.reason')}
                </TableHead>
                <TableHead className="w-[120px] text-end">
                  {t('purchasePages.bankFeed.col.spent')}
                </TableHead>
                <TableHead className="w-[120px] text-end">
                  {t('purchasePages.bankFeed.col.received')}
                </TableHead>
                <TableHead className="w-[150px] text-end">
                  {t('purchasePages.bankFeed.col.action')}
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((l) => (
                <Fragment key={l.id}>
                  <TableRow
                    className={cn(
                      tab === 'for_review' && canEdit && 'cursor-pointer',
                      open === l.id && 'bg-accent/40',
                    )}
                    onClick={() => {
                      if (tab === 'for_review' && canEdit) setOpen(open === l.id ? null : l.id);
                    }}
                    data-testid="bank-line"
                  >
                    {canEdit ? (
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          className="size-4 accent-primary"
                          aria-label={t('purchasePages.bankFeed.selectLine', {
                            description: l.description,
                          })}
                          checked={checked.has(l.id)}
                          onChange={() => toggle(l.id)}
                        />
                      </TableCell>
                    ) : null}
                    <TableCell className="tnum">
                      <span className="inline-flex items-center gap-1">
                        {tab === 'for_review' && canEdit ? (
                          open === l.id ? (
                            <ChevronDown className="size-3.5 text-muted-foreground" />
                          ) : (
                            <ChevronRight className="rtl:-scale-x-100 size-3.5 text-muted-foreground" />
                          )
                        ) : null}
                        {displayDate(l.date)}
                      </span>
                    </TableCell>
                    <TableCell>
                      <div className="font-medium">{l.description}</div>
                      {l.payee && l.payee !== l.description ? (
                        <div className="text-xs text-muted-foreground">{l.payee}</div>
                      ) : null}
                    </TableCell>
                    <TableCell>
                      {l.contactName ??
                        (l.suggestion?.kind === 'rule' ? l.suggestion.contactName : '') ??
                        ''}
                    </TableCell>
                    <TableCell>
                      <CategoryCell line={l} tab={tab} />
                    </TableCell>
                    <TableCell className="tnum text-end">{formatAmount(l.spent)}</TableCell>
                    <TableCell className="tnum text-end">{formatAmount(l.received)}</TableCell>
                    <TableCell className="text-end" onClick={(e) => e.stopPropagation()}>
                      <ActionCell
                        line={l}
                        tab={tab}
                        canEdit={canEdit}
                        busy={batch.isPending}
                        onOpen={() => setOpen(open === l.id ? null : l.id)}
                        onAccept={() => single('accept', l.id)}
                        onUndo={() => single('undo', l.id)}
                      />
                    </TableCell>
                  </TableRow>
                  {open === l.id ? (
                    <TableRow className="hover:bg-transparent">
                      <TableCell colSpan={canEdit ? 8 : 7} className="p-0 whitespace-normal">
                        <LineEditor
                          line={l}
                          bank={bank}
                          banks={banks}
                          onDone={() => setOpen(null)}
                        />
                      </TableCell>
                    </TableRow>
                  ) : null}
                </Fragment>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </>
  );
}

function CategoryCell({ line, tab }: { line: StatementLine; tab: LineStatus }) {
  const t = useT();
  const names = useAccountNames();
  if (tab === 'categorised')
    return (
      <div className="flex flex-col gap-0.5">
        <span className="truncate">{line.categoryLabel}</span>
        <span className="flex gap-1">
          {line.ruleName ? (
            <Badge
              variant="secondary"
              title={
                line.autoAdded
                  ? t('purchasePages.bankFeed.autoAddedTitle')
                  : t('purchasePages.bankFeed.acceptedFromRule')
              }
            >
              {line.autoAdded
                ? t('purchasePages.bankFeed.autoAddedRule', { rule: line.ruleName })
                : t('purchasePages.bankFeed.ruleBadge', { rule: line.ruleName })}
            </Badge>
          ) : null}
          {line.reconciled ? (
            <Badge variant="outline">{t('purchasePages.bankFeed.reconciled')}</Badge>
          ) : null}
        </span>
      </div>
    );
  if (tab === 'excluded')
    return <span className="text-muted-foreground">{t('purchasePages.bankFeed.excluded')}</span>;
  const s = line.suggestion;
  if (!s)
    return (
      <span className="text-muted-foreground">{t('purchasePages.bankFeed.uncategorised')}</span>
    );
  if (s.kind === 'match')
    return (
      <span className="inline-flex flex-col">
        <Badge className="bg-success text-white">{t('purchasePages.bankFeed.oneMatch')}</Badge>
        <span className="mt-0.5 text-xs text-muted-foreground">
          {s.candidate.type} {s.candidate.reference ?? ''} · {displayDate(s.candidate.date)}
        </span>
      </span>
    );
  if (s.kind === 'ai')
    return (
      <span className="inline-flex flex-col gap-0.5" data-testid="ai-suggestion">
        <span className="truncate">
          {s.action === 'transfer'
            ? t('purchasePages.bankFeed.transferTo', {
                account: names.codeName(s.accountId, s.accountName),
              })
            : names.codeName(s.accountId, s.accountName)}
        </span>
        <span className="inline-flex items-center gap-1">
          <Badge variant="secondary">{t('purchasePages.bankFeed.ai')}</Badge>
          <ConfidenceChip field={aiField(s)} />
        </span>
      </span>
    );
  return (
    <span className="inline-flex flex-col gap-0.5">
      <span className="truncate">
        {s.action === 'exclude'
          ? t('purchasePages.bankLabels.exclude')
          : s.accountName && names.codeName(s.accountId, s.accountName)}
      </span>
      <Badge variant="secondary" className="max-w-full truncate">
        {t('purchasePages.bankFeed.ruleBadge', { rule: s.ruleName })}
      </Badge>
    </span>
  );
}

/** An AI suggestion as the confidence chip's field (value, confidence, source, alternatives). */
function aiField(s: AiSuggestion) {
  return {
    value: s.accountId,
    confidence: s.confidence,
    source: s.source,
    alternatives: s.alternatives.map((a) => ({
      value: a.accountId,
      label: a.label,
      probability: a.probability,
    })),
  };
}

function ActionCell({
  line,
  tab,
  canEdit,
  busy,
  onOpen,
  onAccept,
  onUndo,
}: {
  line: StatementLine;
  tab: LineStatus;
  canEdit: boolean;
  busy: boolean;
  onOpen: () => void;
  onAccept: () => void;
  onUndo: () => void;
}) {
  const t = useT();
  const view = line.journalEntryId ? (
    <Button size="sm" variant="link" className="px-2" asChild>
      <Link to="/accounting/journal-entries/$entryId" params={{ entryId: line.journalEntryId }}>
        {t('purchasePages.bankLabels.view')}
      </Link>
    </Button>
  ) : null;
  if (!canEdit) return view;
  if (tab === 'for_review') {
    const s = line.suggestion;
    if (s?.kind === 'match')
      return (
        <Button size="sm" variant="outline" disabled={busy} onClick={onAccept}>
          {t('purchasePages.bankLabels.match')}
        </Button>
      );
    if (s?.kind === 'rule')
      return (
        <Button size="sm" variant="outline" disabled={busy} onClick={onAccept}>
          {s.action === 'exclude'
            ? t('purchasePages.bankLabels.exclude')
            : t('purchasePages.bankLabels.add')}
        </Button>
      );
    if (s?.kind === 'ai' && s.confidence >= AI_ACCEPT_CONFIDENCE)
      return (
        <Button size="sm" variant="outline" disabled={busy} onClick={onAccept}>
          {s.action === 'transfer'
            ? t('purchasePages.bankLabels.transfer')
            : t('purchasePages.bankLabels.add')}
        </Button>
      );
    return (
      <Button size="sm" variant="outline" onClick={onOpen}>
        {t('purchasePages.bankLabels.categorise')}
      </Button>
    );
  }
  return (
    <span className="inline-flex items-center">
      {view}
      {line.reconciled ? (
        <Tooltip>
          <TooltipTrigger asChild>
            <span>
              <Button size="sm" variant="outline" disabled>
                {t('purchasePages.bankLabels.undo')}
              </Button>
            </span>
          </TooltipTrigger>
          <TooltipContent>{t('purchasePages.bankFeed.reconciledUndoFirst')}</TooltipContent>
        </Tooltip>
      ) : (
        <Button size="sm" variant="outline" disabled={busy} onClick={onUndo}>
          {t('purchasePages.bankLabels.undo')}
        </Button>
      )}
    </span>
  );
}
